import React, { useEffect, useState } from 'react';
import { colors } from '../../styles/tokens';
import { IndicadoresService } from '../../services/indicadores.service';
import type {
  DashboardFiltros,
  VendedorDetalleProducto,
  VendedorDetalleResponse,
} from '../../types/indicadores.types';
import { fmtInt } from '../../utils/format';
import { estatusBadgeColors } from '../../utils/negados';
import { buildVendedorDetalleSheet, exportXlsx } from '../../utils/exportXlsx';
import { ProductPhoto } from '../global/NegadosCards';
import ExportButton from '../ExportButton';
import ModeloDetalleModal from './ModeloDetalleModal';
import { useEsMovil } from '../../hooks/useEsMovil';

export interface VendedorDetalleModalProps {
  /** `externalUserId` del vendedor sobre cuya card se hizo clic (`Vendedores.tsx`, Task FE-A). */
  vendedorId: string;
  /** Nombre del vendedor — `IndicadoresScreen` lo resuelve del mismo array `vendedores` que ya pinta la card, sin volver a pedirlo. */
  nombre?: string;
  /** Código corto de Proscai (`FAG.AGNUM`) — mismo criterio que `vend-codigo` en la card. */
  codigo?: string;
  /**
   * Rango + tienda YA activos en Mi tienda (`negadosRango`/`miTiendaStore` de
   * `IndicadoresScreen`) — el detalle hereda ESE alcance, igual que
   * `NegadosDrillModal`. A diferencia del app (que nunca manda
   * `chain`/`branchOffice` porque el server los fuerza para `branch_office`),
   * el admin SÍ los envía: ve una tienda arbitraria.
   */
  filtros: DashboardFiltros;
  onClose: () => void;
}

const boxMessageStyle: React.CSSProperties = {
  padding: '20px 16px',
  textAlign: 'center',
  fontWeight: 600,
  fontSize: 13,
};

/** Un dato numérico chico ("Consultó 12") de la fila — reusado para las 4 métricas. */
const StatCell: React.FC<{ label: string; value: number; color?: string }> = ({ label, value, color }) => (
  <div style={{ textAlign: 'center', minWidth: 52 }}>
    <div
      style={{
        fontSize: 15,
        fontWeight: 800,
        color: color ?? colors.texto.primary,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {fmtInt(value)}
    </div>
    <div style={{ fontSize: 9.5, fontWeight: 700, color: colors.texto.quaternary, textTransform: 'uppercase', letterSpacing: '.03em' }}>
      {label}
    </div>
  </div>
);

/**
 * Una fila de la tabla de trazabilidad: foto + descripción/ICOD + estatus +
 * las 4 métricas. Clickeable (Fase D, plan
 * `2026-07-22-detalle-por-modelo-y-export-xlsx-plan`) — abre el modal
 * ANIDADO `ModeloDetalleModal` con la corrida por talla de ESE producto para
 * este vendedor. Accesible como botón (`role`/`tabIndex`/Enter·Espacio, con
 * guard de `e.repeat` para no re-disparar mientras la tecla se mantiene
 * apretada).
 */
const FilaDetalle: React.FC<{ p: VendedorDetalleProducto; onSelect: (p: VendedorDetalleProducto) => void }> = ({
  p,
  onSelect,
}) => {
  const { bg, text } = estatusBadgeColors(p.status);
  // Guard de descripción en blanco (BE-2 §nota): productos que solo existen
  // en la venta de Proscai, sin descripción resuelta, caen al icod.
  const descripcion = p.descripcion || p.icod;

  function handleClick() {
    onSelect(p);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTableRowElement>) {
    if (e.repeat) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(p);
    }
  }

  return (
    <tr
      data-testid={`fila-detalle-${p.icod}`}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      style={{ borderBottom: `1px solid ${colors.rowBorder}`, cursor: 'pointer' }}
    >
      <td style={{ padding: '10px 12px' }}>
        <ProductPhoto imageUrl={p.imageUrl} alt={descripcion} />
      </td>
      <td style={{ padding: '10px 12px' }}>
        {/* `fila-detalle-descr-${icod}` acota la aserción del guard de
            descripción-en-blanco al nodo de la descripción PRINCIPAL (no a la
            sublínea "ICOD ..." que SIEMPRE muestra el icod) — así el test del
            fallback deja de ser tautológico. */}
        <div
          data-testid={`fila-detalle-descr-${p.icod}`}
          style={{ fontWeight: 700, color: colors.texto.strong, fontSize: 13 }}
        >
          {descripcion}
        </div>
        <div style={{ fontSize: 11, color: colors.texto.quaternary, fontWeight: 600, marginTop: 1 }}>
          ICOD {p.icod}
        </div>
      </td>
      <td style={{ padding: '10px 8px' }}>
        <span
          style={{
            display: 'inline-block',
            fontSize: 10.5,
            fontWeight: 700,
            padding: '3px 8px',
            borderRadius: 6,
            background: bg,
            color: text,
            whiteSpace: 'nowrap',
          }}
        >
          {p.status}
        </span>
      </td>
      <td style={{ padding: '8px 10px' }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <StatCell label="Consultó" value={p.consultas} />
          <StatCell label="Surtió" value={p.atendidas} color={colors.serie.surtio} />
          <StatCell label="Negó" value={p.negados} color={colors.serie.negado} />
          <StatCell label="Vendió" value={p.vendido} color={colors.brand} />
        </div>
      </td>
    </tr>
  );
};

/**
 * Modal de detalle/trazabilidad por producto de UN vendedor (Task FE-C, plan
 * `2026-07-22-tarjeta-vendedor-tasa-y-trazabilidad-plan`) — se abre al hacer
 * clic en una card de `Vendedores.tsx` (Task FE-A conecta `onSelectVendedor`).
 * Espejo del app (Task FE-B) — mismos testids, mismos colores, misma lógica de
 * fila; solo difiere el wrapper de fetch: acá `IndicadoresService.getVendedorDetalle`
 * + `useEffect`/`useState` (el app usa RTK `useGetVendedorDetalleQuery`), y el
 * admin manda `chain`/`branchOffice` en `filtros` (ve una tienda arbitraria).
 * Copia el shell de `NegadosDrillModal` (backdrop + Esc + botón de cierre,
 * estados loading/error/vacío) y reusa la foto CloudFront (`ProductPhoto`) +
 * el badge de estatus (`estatusBadgeColors`) de `NegadosTiendaTable`.
 *
 * `nombre`/`codigo` vienen de la card que abrió el modal (`IndicadoresScreen`
 * los resuelve de `tienda.vendedores`, mismo array que ya pinta `Vendedores`) —
 * este componente NO los vuelve a pedir al backend.
 *
 * Modal ANIDADO por modelo (Fase D, plan
 * `2026-07-22-detalle-por-modelo-y-export-xlsx-plan`, espejo del app): el
 * estado de "qué producto está abierto en el modal por modelo" se maneja
 * LOCALMENTE aquí (`modeloSeleccionado`, self-contained — `IndicadoresScreen`
 * no se entera). Un clic (o Enter/Espacio) en una fila abre `ModeloDetalleModal`
 * encima; hereda `vendedorId`, el `icod`/`descripcion` de la fila, el `nombre`
 * de este modal y el MISMO `filtros` (incluye `chain`/`branchOffice`, a
 * diferencia del app).
 */
const VendedorDetalleModal: React.FC<VendedorDetalleModalProps> = ({
  vendedorId,
  nombre,
  codigo,
  filtros,
  onClose,
}) => {
  const esMovil = useEsMovil();
  const [data, setData] = useState<VendedorDetalleResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [modeloSeleccionado, setModeloSeleccionado] = useState<{ icod: string; descripcion: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);

    IndicadoresService.getVendedorDetalle(vendedorId, filtros)
      .then((resp) => {
        if (!cancelled) {
          setData(resp);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // Deps en campos PRIMITIVOS de `filtros` (no el objeto completo): mismo
    // criterio que `NegadosDrillModal` — `filtros` llega como un literal
    // `{...}` que `IndicadoresScreen` re-crea en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendedorId, filtros.desde, filtros.hasta, filtros.chain, filtros.branchOffice]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        // Mientras el modal anidado por modelo está abierto, Esc lo cierra A
        // ÉL (su propio listener en `ModeloDetalleModal`) — este modal se
        // abstiene para que UNA pulsación de Esc no cierre los DOS de un
        // jalón (ambos escuchan `keydown` en el mismo `document`).
        if (modeloSeleccionado) return;
        onClose();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, modeloSeleccionado]);

  const productos = data?.productos ?? [];
  const vacio = !loading && !error && productos.length === 0;
  const titulo = nombre ? `${nombre}${codigo ? ` · ${codigo}` : ''}` : 'Vendedor';
  const rango = data?.rango;

  /** Export a Excel de la hoja "Detalle vendedor" (mismo `productos` ya cargado, sin re-fetch). */
  function handleExport() {
    const base = (nombre || vendedorId).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    const filename = rango ? `detalle-${base}_${rango.desde}_${rango.hasta}.xlsx` : `detalle-${base}.xlsx`;
    return exportXlsx([buildVendedorDetalleSheet(productos)], filename);
  }

  return (
    <>
      <div
        data-testid="vendedor-detalle-backdrop"
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(20,23,29,.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 100,
          // En telefono 20 px por lado se comen 40 de los 390: el modal se
          // pega mas al borde para dejarle el ancho a la tabla de dentro.
          padding: esMovil ? 10 : 20,
        }}
      >
      <div
        data-testid="vendedor-detalle-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: colors.card,
          borderRadius: 16,
          border: `1px solid ${colors.border}`,
          boxShadow: '0 20px 48px -14px rgba(20,28,54,.45)',
          width: '100%',
          maxWidth: 680,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            background: colors.brand,
            color: '#fff',
            padding: esMovil ? '12px 14px' : '14px 20px',
            display: 'flex',
            alignItems: 'center',
            // En telefono el titulo se lleva la primera linea entera y los
            // controles (Excel + x) bajan a la segunda: en una sola fila de
            // 390 px el titulo quedaba en dos o tres caracteres. `wrap` lo
            // consigue sin meter ningun nodo nuevo, asi que en escritorio la
            // barra es exactamente la misma fila de siempre.
            flexWrap: esMovil ? 'wrap' : undefined,
            gap: esMovil ? 8 : 14,
          }}
        >
          <div style={{ flex: esMovil ? '1 1 100%' : 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 16, letterSpacing: '.01em' }}>{titulo}</div>
            <div style={{ fontSize: 11.5, opacity: 0.72, fontWeight: 600, marginTop: 2 }}>
              Detalle de actividad por producto{rango ? ` · ${rango.desde}–${rango.hasta}` : ''}
            </div>
          </div>
          <ExportButton
            testid="vendedor-detalle"
            variant="dark"
            onExport={handleExport}
            disabled={productos.length === 0}
          />
          <button
            type="button"
            data-testid="vendedor-detalle-cerrar"
            onClick={onClose}
            aria-label="Cerrar"
            style={{
              background: 'rgba(255,255,255,.14)',
              border: 'none',
              borderRadius: 8,
              color: '#fff',
              fontSize: 18,
              lineHeight: 1,
              cursor: 'pointer',
              padding: esMovil ? '0 12px' : '4px 9px',
              // Regla 4: 44 px de area tocable en telefono.
              ...(esMovil ? { minHeight: 44, minWidth: 44, fontSize: 22, marginLeft: 'auto' } : null),
            }}
          >
            ×
          </button>
        </div>

        {loading && (
          <div data-testid="vendedor-detalle-loading" style={{ ...boxMessageStyle, color: colors.texto.tertiary }}>
            Cargando detalle…
          </div>
        )}

        {!loading && error && (
          <div data-testid="vendedor-detalle-error" style={{ ...boxMessageStyle, color: colors.rojo }}>
            No se pudo cargar el detalle. Reintenta.
          </div>
        )}

        {vacio && (
          <div data-testid="vendedor-detalle-vacio" style={{ ...boxMessageStyle, color: colors.texto.tertiary }}>
            Este vendedor no tiene actividad por producto en el periodo.
          </div>
        )}

        {!loading && !error && !vacio && (
          <div style={{ overflow: 'auto', padding: '4px 0 16px' }}>
            <table data-testid="vendedor-detalle-tabla" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <tbody>
                {productos.map((p) => (
                  <FilaDetalle
                    key={p.icod}
                    p={p}
                    onSelect={(prod) => setModeloSeleccionado({ icod: prod.icod, descripcion: prod.descripcion })}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </div>
      {modeloSeleccionado && (
        <ModeloDetalleModal
          vendedorId={vendedorId}
          icod={modeloSeleccionado.icod}
          descripcion={modeloSeleccionado.descripcion}
          nombre={nombre}
          filtros={filtros}
          onClose={() => setModeloSeleccionado(null)}
        />
      )}
    </>
  );
};

export default VendedorDetalleModal;
